import classNames from 'classnames';

import { currencyFormatter } from '@/utils/currencyFormatter';

export interface InvoiceDetailsProps {
  locale?: string;
  title?: string;
  timestamp?: string;
  timestampPrefix?: string;
  amountCents?: number;
  originalAmountCents?: number;
  currency?: string;
  interval?: string;
  className?: string;
  placeholderText?: string;
  discount?: number;
}

const dateFormatter = ({ locale = 'en-US' }: { locale?: string }) =>
  new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });

export function InvoiceDetails({
  title,
  timestamp,
  timestampPrefix,
  locale,
  amountCents,
  originalAmountCents,
  currency,
  interval,
  placeholderText,
  className,
  discount,
}: InvoiceDetailsProps) {
  const formattedTimestamp = timestamp ? dateFormatter({ locale }).format(new Date(timestamp)) : placeholderText;
  const hasDiscount = typeof discount === 'number' && discount > 0;

  return (
    <div
      className={classNames(
        'bg-[#F9FAFB] rounded-md border border-[#E5E7EB] p-4 flex flex-col gap-4 w-full',
        className,
      )}
    >
      <div className="flex flex-col">
        <div className="text-sm font-normal text-gray-900 leading-5">{title}</div>
        <div className="text-sm font-light text-gray-600 leading-5">
          {timestampPrefix} {formattedTimestamp}
        </div>
      </div>
      <div className="flex items-end justify-between w-full">
        {hasDiscount && typeof amountCents === 'number' && typeof originalAmountCents === 'number' ? (
          <div className="flex items-end gap-2">
            <span className="text-xl font-medium text-gray-500 leading-7 line-through">
              {currencyFormatter({ value: originalAmountCents / 100, currency: currency || 'USD', locale })}
            </span>
            <span className="text-3xl font-semibold text-gray-900 leading-9">
              {currencyFormatter({ value: amountCents / 100, currency: currency || 'USD', locale })}
            </span>
          </div>
        ) : (
          <span className="text-3xl font-semibold text-gray-900 leading-9">
            {typeof amountCents === 'number'
              ? currencyFormatter({ value: amountCents / 100, currency: currency || 'USD', locale })
              : placeholderText}
          </span>
        )}
        {interval && <span className="text-sm leading-5 font-light text-gray-600">/{interval}</span>}
      </div>
    </div>
  );
}
